<template>
  <button
    ref="card"
    type="button"
    class="pedido-kanban-card"
    :class="{
      'is-active': active,
      'is-urgent': isUrgent,
      'has-alert': alertText,
    }"
    :aria-label="ariaLabel"
    :aria-current="active ? 'true' : null"
    @click="$emit('select', order, $event.currentTarget)"
  >
    <span class="pedido-card-topline">
      <span
        v-if="draggable"
        class="pedido-card-drag-handle"
        title="Arraste para mudar a etapa do pedido"
        aria-hidden="true"
        @click.stop
        ><i class="fas fa-grip-vertical"></i
      ></span>
      <strong>{{ code }}</strong>
      <span class="pedido-stage-age" :title="stageTitle">
        <i class="far fa-clock" aria-hidden="true"></i>
        {{ ageLabel }}
      </span>
    </span>

    <span class="pedido-card-customer">{{ customer }}</span>

    <span class="pedido-card-summary">
      <span>
        <i :class="deliveryIcon" aria-hidden="true"></i>
        {{ deliveryLabel }}
      </span>
      <strong>{{ money(order.total) }}</strong>
    </span>

    <span class="pedido-card-meta">
      <span>{{ itemCountLabel }}</span>
      <span :class="paymentTone">{{ paymentLabel }}</span>
    </span>

    <span v-if="operationalText" class="pedido-card-operation">
      <i :class="operationalIcon" aria-hidden="true"></i>
      {{ operationalText }}
    </span>

    <span v-if="alertText" class="pedido-card-alert" role="status">
      <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
      {{ alertText }}
    </span>
  </button>
</template>

<script>
import { paymentMethodLabel } from "../../services/onlineOrder";
import {
  formatDuration,
  formatMoney,
  orderCode,
  orderCustomer,
  stageAgeSeconds,
} from "./orderBoard";

export default {
  name: "PedidoKanbanCard",
  props: {
    order: { type: Object, required: true },
    serverTime: { type: String, default: null },
    now: { type: Number, default: () => Date.now() },
    active: { type: Boolean, default: false },
    draggable: { type: Boolean, default: false },
  },
  computed: {
    code() {
      return orderCode(this.order);
    },
    customer() {
      return orderCustomer(this.order);
    },
    ageSeconds() {
      return stageAgeSeconds(this.order, this.serverTime, this.now);
    },
    ageLabel() {
      return formatDuration(this.ageSeconds);
    },
    stageTitle() {
      return `Nesta etapa há ${this.ageLabel}`;
    },
    isUrgent() {
      const limits = {
        novos: 10 * 60,
        producao: 30 * 60,
        retirada: 20 * 60,
        entrega: 25 * 60,
      };
      const limit = limits[this.order.lane];
      return Boolean(limit && this.ageSeconds >= limit);
    },
    deliveryLabel() {
      return this.order.tipo_entrega === "delivery" ? "Entrega" : "Retirada";
    },
    deliveryIcon() {
      return this.order.tipo_entrega === "delivery"
        ? "fas fa-motorcycle"
        : "fas fa-store";
    },
    itemCountLabel() {
      const count = Number(this.order.item_count || 0);
      return `${count} ${count === 1 ? "item" : "itens"}`;
    },
    payment() {
      return this.order.payment || {};
    },
    paymentLabel() {
      const method = paymentMethodLabel(
        this.payment.metodo,
        this.order.tipo_entrega
      );
      const status = this.payment.status;
      if (["pago", "aprovado"].includes(status)) return `${method} · Pago`;
      if (["cancelado", "expirado", "falhou"].includes(status)) {
        return `${method} · ${status}`;
      }
      return `${method} · Pendente`;
    },
    paymentTone() {
      if (["pago", "aprovado"].includes(this.payment.status)) {
        return "payment-paid";
      }
      if (["cancelado", "expirado", "falhou"].includes(this.payment.status)) {
        return "payment-failed";
      }
      return "payment-pending";
    },
    production() {
      return this.order.production || {};
    },
    delivery() {
      return this.order.delivery || {};
    },
    operationalText() {
      if (this.order.lane === "producao") {
        const done = Number(
          this.production.ready ??
            this.production.completed ??
            this.production.concluidas ??
            0
        );
        const total = Number(
          this.production.total ?? this.production.filas ?? 0
        );
        if (total > 0) return `Produção ${done}/${total}`;
        return "Produção em andamento";
      }

      if (this.order.lane === "entrega") {
        return (
          this.delivery.status_label ||
          this.delivery.situacao_label ||
          this.delivery.courier?.name ||
          this.delivery.entregador_nome ||
          (this.order.status === "saiu_para_entrega"
            ? "Em rota"
            : "Aguardando expedição")
        );
      }

      return null;
    },
    operationalIcon() {
      return this.order.lane === "entrega" ? "fas fa-route" : "fas fa-utensils";
    },
    alertText() {
      const blockers = this.order.blocking_reasons;
      if (Array.isArray(blockers) && blockers.length) {
        const first = blockers[0];
        return typeof first === "string"
          ? first
          : first.message || first.label || "Ação necessária";
      }
      const failedPaymentLabels = {
        cancelado: "Pagamento cancelado",
        expirado: "Pagamento expirado",
        falhou: "Falha no pagamento",
      };
      if (failedPaymentLabels[this.payment.status]) {
        return failedPaymentLabels[this.payment.status];
      }
      if (
        !["pago", "aprovado"].includes(this.payment.status) &&
        ["retirada", "entrega"].includes(this.order.lane)
      ) {
        return "Pagamento pendente";
      }
      if (this.isUrgent) return "Tempo de espera acima do esperado";
      return null;
    },
    ariaLabel() {
      const alert = this.alertText ? `. Atenção: ${this.alertText}` : "";
      return `${this.code}, ${this.customer}, ${
        this.deliveryLabel
      }, ${this.money(this.order.total)}, ${this.ageLabel} nesta etapa${alert}`;
    },
  },
  methods: {
    money: formatMoney,
  },
};
</script>

<style scoped>
.pedido-card-drag-handle {
  cursor: grab;
  flex: 0 0 auto;
  color: #7e8299;
  padding: 6px 8px;
  touch-action: none;
}
.pedido-card-drag-handle:active {
  cursor: grabbing;
}
.pedido-kanban-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-left: 4px solid transparent;
  border-radius: 10px;
  color: #3f4254;
  display: flex;
  flex-direction: column;
  gap: 9px;
  max-width: 100%;
  min-height: 148px;
  min-width: 0;
  overflow: hidden;
  padding: 14px;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;
  width: 100%;
}

.pedido-kanban-card:hover {
  border-color: #b5b5c3;
  box-shadow: 0 5px 18px rgba(24, 28, 50, 0.08);
  transform: translateY(-1px);
}

.pedido-kanban-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.28);
  outline: 2px solid #3699ff;
  outline-offset: 2px;
}

.pedido-kanban-card.is-active {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.14);
}

.pedido-kanban-card.is-urgent {
  border-left-color: #f64e60;
}

.pedido-card-topline,
.pedido-card-summary,
.pedido-card-operation,
.pedido-card-alert {
  align-items: center;
  display: flex;
}

.pedido-card-topline,
.pedido-card-summary {
  gap: 10px;
  justify-content: space-between;
  min-width: 0;
}

.pedido-card-meta {
  display: grid;
  gap: 8px;
  grid-template-columns: auto minmax(0, 1fr);
  min-width: 0;
}

.pedido-card-topline strong {
  color: #181c32;
  font-size: 13px;
  letter-spacing: 0.01em;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pedido-stage-age {
  color: #7e8299;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.pedido-card-customer {
  color: #181c32;
  font-size: 15px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pedido-card-summary {
  font-size: 13px;
}

.pedido-card-summary > span,
.pedido-card-operation {
  color: #5e6278;
  gap: 6px;
  min-width: 0;
}

.pedido-card-summary > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pedido-card-summary strong {
  color: #181c32;
  flex: 0 0 auto;
  white-space: nowrap;
}

.pedido-card-meta {
  border-top: 1px solid #f0f1f5;
  color: #7e8299;
  font-size: 11px;
  font-weight: 700;
  padding-top: 8px;
}

.pedido-card-meta > span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.pedido-card-meta > span:last-child {
  justify-self: end;
  max-width: 100%;
  text-align: right;
}

.payment-paid {
  color: #0b8d68;
}

.payment-pending {
  color: #c27c00;
}

.payment-failed {
  color: #c93649;
}

.pedido-card-operation {
  background: #f3f6f9;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  padding: 7px 9px;
  overflow-wrap: anywhere;
}

.pedido-card-alert {
  color: #c93649;
  font-size: 11px;
  font-weight: 800;
  gap: 6px;
  overflow-wrap: anywhere;
}
</style>
